// TCPA Survival Guide landing — captures email/name → POST /api/lead-magnet/tcpa-guide
// → on success shows download link + "check your inbox" success state, and
// enrolls the visitor in the follow-up drip. Sister landing page to FreeEbook.jsx
// (the Call Coach playbook); same visual language, different fear driver:
//   Call Coach ebook = aspirational ("do better")
//   TCPA Guide      = defensive ("don't get sued")
//
// Design intent: shares the dark-navy cover + signal-green accents with the
// Call Coach landing for brand coherence.

import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { track, captureUtm } from "@/lib/analytics";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import MoreEbooksStrip from "@/components/MoreEbooksStrip";
import {
  ArrowRight,
  Mail,
  User as UserIcon,
  Download,
  CheckCircle2,
  BookOpen,
  Brain,
  Headphones,
  Radio,
  Sparkles,
  Star,
  Lock,
  ChevronRight,
} from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${backend}/api/lead-magnet/tcpa-guide.pdf`;
const CAPTURE_URL = `${backend}/api/lead-magnet/tcpa-guide`;

const CHAPTERS = [
  { n: "01", t: "The TCPA in 90 seconds — and why your CRM is a target" },
  { n: "02", t: "Why your skip-trace data is a legal landmine" },
  { n: "03", t: "&quot;Express written consent&quot; — what actually counts in court" },
  { n: "04", t: "The four message patterns that trigger lawsuits" },
  { n: "05", t: "Three real wholesaler lawsuits — outcomes and settlements" },
  { n: "06", t: "The 5-layer TCPA-Safe Outbound stack" },
  { n: "07", t: "Building an audit trail that wins motions to dismiss" },
  { n: "08", t: "If you receive a TCPA demand letter — exact 4-step playbook" },
  { n: "09", t: "Your 7-day compliance audit checklist" },
];

const PROOF_POINTS = [
  { k: "14 pp", v: "of defensive playbook" },
  { k: "$1,500", v: "per text — the statutory damage" },
  { k: "247M+", v: "phone numbers on the DNC list today" },
  { k: "5 layers", v: "of TCPA-safe controls" },
];

const REVIEW_AVATARS = [
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=faces&fit=crop&w=120&h=120&q=80",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=faces&fit=crop&w=120&h=120&q=80",
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=faces&fit=crop&w=120&h=120&q=80",
];

export default function TcpaGuide() {
  useEffect(() => {
    captureUtm();
    track("tcpa_guide_view");
  }, []);

  return (
    <div className="mkt-shell bg-[#F8FAFC] min-h-screen">
      <EbookNav />
      <Hero />
      <ProofStrip />
      <Inside />
      <Testimony />
      <FinalCTA />
      <MoreEbooksStrip currentSlug="tcpa-guide" />
      <Footer />
    </div>
  );
}

function EbookNav() {
  return (
    <header className="mkt-nav border-b border-[var(--line)]">
      <div className="mkt-nav-inner">
        <Link to="/" className="flex items-center gap-2" data-testid="ebook-brand">
          <img src="/brand/wordmark-color.png" alt="iSpeedToClose" className="h-14 md:h-16 w-auto" width="243" height="64" />
        </Link>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/" className="btn-ghost" data-testid="ebook-back-landing">
            ← Back to home
          </Link>
          <Link to="/register" className="btn-money" data-testid="ebook-nav-register">
            Try free <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [captureId, setCaptureId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.email.includes("@") || form.name.trim().length < 1) {
      setErrorMsg("Please enter your name and a valid email.");
      return;
    }
    setStatus("submitting");
    track("tcpa_guide_submit_attempt");
    try {
      const r = await fetch(CAPTURE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          source: "tcpa-guide",
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCaptureId(data.capture_id);
      setStatus("success");
      track("tcpa_guide_capture_success", { id: data.capture_id });
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong — try again or use the direct download below.");
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220] text-white">
      {/* Atmospheric glows */}
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[var(--primary)]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 w-[30rem] h-[30rem] rounded-full bg-amber-400/8 blur-3xl pointer-events-none" />
      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(16,185,129,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
        {/* LEFT — pitch + form */}
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/15 border border-[var(--primary)]/30 rounded-full px-3 py-1 mb-6">
            <Lock size={12} /> Free 14-page Compliance Field Guide
          </div>
          <h1 className="font-display font-black tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl mb-5">
            How to send <span className="text-[var(--primary)]">10,000+ texts</span><br />
            without getting sued.
          </h1>
          <p className="text-base lg:text-lg text-white/75 leading-relaxed mb-7 max-w-xl">
            The TCPA awards plaintiffs <b className="text-white">$500 per text — $1,500 for willful violations.</b>
            A single bad skip-trace list with 200 numbers turns into a $300,000 lawsuit overnight.
            This guide is the same defensive stack iSpeedToClose implements automatically — built
            from FCC rules, court records, and real wholesaler settlements.
          </p>

          {/* Bullet teasers */}
          <ul className="space-y-2.5 mb-8 max-w-xl">
            {[
              "The 5-layer TCPA-Safe Outbound stack (DNC + consent + audit + opt-out + quiet hours)",
              "Three real wholesaler lawsuits — including the $57M judgment that bankrupted a 3-person team",
              "&quot;Express written consent&quot; — what actually counts vs. what gets dismissed",
              "Your 7-day compliance audit checklist + 4-step demand-letter playbook",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm lg:text-[15px] text-white/85">
                <span className="mt-[5px] w-4 h-4 rounded-full bg-[var(--primary)]/20 text-[var(--primary)] border border-[var(--primary)]/40 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={11} />
                </span>
                <span dangerouslySetInnerHTML={{ __html: b }} />
              </li>
            ))}
          </ul>

          {/* Capture form OR success state */}
          {status !== "success" ? (
            <form
              onSubmit={submit}
              className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-5 max-w-xl"
              data-testid="ebook-capture-form"
            >
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="sr-only">Your name</span>
                  <div className="relative">
                    <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      required
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full pl-9 pr-3 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-[var(--primary)]/60 text-sm"
                      data-testid="ebook-input-name"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="sr-only">Email</span>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      required
                      placeholder="you@yourbiz.com"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full pl-9 pr-3 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-[var(--primary)]/60 text-sm"
                      data-testid="ebook-input-email"
                    />
                  </div>
                </label>
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full btn-money !py-3 !text-[15px] disabled:opacity-60"
                data-testid="ebook-submit"
              >
                {status === "submitting" ? "Sending…" : (
                  <>Send me the playbook <ArrowRight size={14} /></>
                )}
              </button>
              {errorMsg && (
                <div className="mt-3 text-xs text-rose-300" data-testid="ebook-error">{errorMsg}</div>
              )}
              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/55">
                <Lock size={11} /> Name + email only · no credit card · unsubscribe any time
              </div>
            </form>
          ) : (
            <div
              className="bg-[var(--primary)]/12 border border-[var(--primary)]/35 rounded-2xl p-5 max-w-xl"
              data-testid="ebook-success"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--primary)] text-[#0B1220] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg text-white mb-1">
                    Check your inbox.
                  </div>
                  <p className="text-sm text-white/75 leading-relaxed mb-3">
                    We just sent the PDF to <b className="text-white">{form.email}</b>. If it doesn't show up in a
                    minute, peek in spam — or grab it directly here:
                  </p>
                  <a
                    href={PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-[#0B1220] font-bold px-4 py-2.5 rounded-lg text-sm hover:bg-white/90 transition"
                    data-testid="ebook-direct-download"
                    onClick={() => track("free_ebook_direct_download", { id: captureId })}
                  >
                    <Download size={14} /> Download the PDF
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — 3D-ish cover mockup */}
        <div className="hidden lg:block">
          <EbookCoverMockup />
        </div>
      </div>
    </section>
  );
}

// ─── Animated cover mockup (CSS-only, no extra assets) ──────────────────
function EbookCoverMockup() {
  return (
    <div className="relative w-full max-w-[420px] mx-auto">
      {/* Spine shadow */}
      <div
        className="absolute inset-y-6 -right-3 w-3 rounded-r-md bg-gradient-to-r from-black/40 to-black/0"
        aria-hidden
      />
      {/* Floor shadow */}
      <div
        className="absolute -bottom-10 left-6 right-6 h-10 bg-black/40 blur-2xl rounded-full"
        aria-hidden
      />
      {/* The cover — matches the actual PDF cover */}
      <div
        className="relative aspect-[0.77/1] rounded-2xl overflow-hidden ring-1 ring-white/10"
        style={{
          background: "linear-gradient(135deg, #0B1220 0%, #0F172A 55%, #0B1220 100%)",
          transform: "rotate(-3deg) translateX(-6px)",
          boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7), 0 10px 25px -10px rgba(16,185,129,0.25)",
        }}
      >
        {/* Decorative glows */}
        <div
          className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.45), transparent 60%)" }}
          aria-hidden
        />
        <div
          className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.4), transparent 60%)" }}
          aria-hidden
        />
        {/* Accent stripe */}
        <div className="absolute inset-y-0 left-0 w-1 bg-[var(--primary)]" aria-hidden />
        {/* Cover content */}
        <div className="absolute inset-0 p-8 flex flex-col">
          <div className="flex items-center gap-2 text-white/90">
            <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
              <AssignFlowLogo size={14} />
            </span>
            <span className="font-display font-extrabold text-sm">iSpeedToClose</span>
          </div>
          <div className="text-[10px] tracking-[0.18em] text-white/45 mt-1 ml-9">
            OPERATOR PLAYBOOK · 2026
          </div>

          <div className="mt-auto">
            <div className="text-[11px] font-bold tracking-[0.18em] text-[var(--primary)] mb-3">
              COMPLIANCE FIELD GUIDE · 14 PAGES
            </div>
            <div className="font-display font-black text-white tracking-tight leading-[0.95] text-[40px]">
              TCPA<br />Survival<br />Guide.
            </div>
            <p className="mt-5 text-[12px] text-white/55 leading-snug max-w-[16rem]">
              Send 10,000+ texts a month without getting hit by $500–$1,500-per-message lawsuits.
              FCC rules, real case settlements, and the 5-layer defensive stack.
            </p>
            <div className="mt-6 text-[10px] tracking-[0.16em] text-white/35 italic">
              WRITTEN FOR WHOLESALERS · DEFENSIVE · FREE
            </div>
          </div>
        </div>
      </div>

      {/* Floating "TCPA-safe" badge */}
      <div className="absolute -top-3 -left-3 bg-white rounded-full shadow-xl px-4 py-2 flex items-center gap-2 ring-1 ring-black/5"
           style={{ transform: "rotate(-8deg)" }}>
        <Lock size={14} className="text-[var(--primary-dark)]" />
        <span className="text-[12px] font-bold text-[var(--ink)]">Audit-ready</span>
      </div>
      {/* Floating "free" sticker */}
      <div className="absolute -bottom-4 -right-2 bg-[var(--primary)] text-[#0B1220] rounded-full shadow-xl px-4 py-2 flex items-center gap-2 ring-2 ring-[var(--primary)]/30"
           style={{ transform: "rotate(6deg)" }}>
        <span className="text-[11px] font-black tracking-widest uppercase">100% free</span>
      </div>
    </div>
  );
}

// ─── Proof strip ────────────────────────────────────────────────────────
function ProofStrip() {
  return (
    <section className="bg-white border-y border-[var(--line)]" data-testid="ebook-proof-strip">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {PROOF_POINTS.map((p) => (
          <div key={p.v} className="text-center md:text-left">
            <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter text-[var(--ink)] whitespace-pre-line">
              {p.k}
            </div>
            <div className="text-xs lg:text-sm text-[var(--ink-2)] mt-1 leading-snug">{p.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Inside section — chapter list + visual ─────────────────────────────
function Inside() {
  return (
    <section className="bg-[var(--bg)] py-20 lg:py-24" data-testid="ebook-inside">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-12 items-start">
          <div className="md:sticky md:top-24">
            <div className="mkt-eyebrow">Inside the playbook</div>
            <h2 className="mkt-h2 mb-4">Nine chapters. Real cases. No theory.</h2>
            <p className="text-[var(--ink-2)] text-base lg:text-lg leading-relaxed mb-6">
              Built from FCC consent decrees, federal case dockets, and the actual DNC-guard
              logic powering iSpeedToClose's outbound. Plus a 4-step demand-letter response
              playbook your future self will thank you for.
            </p>

            {/* Defensive stack mini-grid */}
            <div className="grid grid-cols-2 gap-2">
              <PillarChip icon={Lock} label="DNC pre-flight" pct="Layer 1" />
              <PillarChip icon={Sparkles} label="Consent audit" pct="Layer 2" accent />
              <PillarChip icon={Brain} label="Opt-out auto" pct="Layer 3" />
              <PillarChip icon={Headphones} label="Quiet hours" pct="Layer 4" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[var(--line)] shadow-sm overflow-hidden">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.n}
                className={`flex items-center gap-4 px-5 py-4 ${i !== CHAPTERS.length - 1 ? "border-b border-[var(--line)]" : ""}`}
                data-testid={`ebook-chapter-${c.n}`}
              >
                <span className="font-display font-black text-[var(--primary-dark)] text-lg tracking-tighter w-10 flex-shrink-0">
                  {c.n}
                </span>
                <span className="font-medium text-[var(--ink)] text-sm lg:text-[15px] flex-1 leading-snug">
                  {c.t}
                </span>
                <ChevronRight size={14} className="text-[var(--ink-3)] flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PillarChip({ icon: Icon, label, pct, accent }) {
  return (
    <div className={`rounded-xl border px-3 py-3 flex items-center gap-2.5 ${accent ? "bg-[var(--primary)]/10 border-[var(--primary)]/30" : "bg-white border-[var(--line)]"}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent ? "bg-[var(--primary)] text-[#0B1220]" : "bg-[var(--ink)] text-white"}`}>
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-[var(--ink)] truncate">{label}</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">{pct}</div>
      </div>
    </div>
  );
}

// ─── Testimony ──────────────────────────────────────────────────────────
function Testimony() {
  return (
    <section className="bg-white py-20 lg:py-24 border-y border-[var(--line)]" data-testid="ebook-testimony">
      <div className="max-w-[920px] mx-auto px-6 lg:px-8 text-center">
        <div className="flex justify-center gap-1 mb-5">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} className="text-[var(--primary)]" fill="currentColor" />)}
        </div>
        <p className="font-display font-bold text-2xl lg:text-3xl leading-[1.25] tracking-tight text-[var(--ink)]">
          "Plaintiffs' attorneys don't look for evidence you broke the law.
          They look for evidence you have no defense. The audit trail is the defense —
          and you only build it by screening every single send, every single time."
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="flex -space-x-2">
            {REVIEW_AVATARS.map((src, i) => (
              <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
            ))}
          </div>
          <div className="text-left text-sm text-[var(--ink-2)]">
            From three real TCPA case dockets —<br />
            <span className="font-semibold text-[var(--ink)]">$57M, $2.1M, $18.9M</span> in plaintiff wins
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ──────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="mkt-section" data-testid="ebook-final-cta">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--ink)] via-[#0F172A] to-[#0B1220] text-white p-10 lg:p-16">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--primary)]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/15 border border-[var(--primary)]/30 rounded-full px-3 py-1 mb-5">
            <Lock size={11} /> Want this protection automatically?
          </div>
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight leading-[1.05] mb-4">
            Stop bleeding $1,500/text.<br />Ship safe by default.
          </h2>
          <p className="text-white/75 text-base lg:text-lg leading-relaxed mb-7">
            Every defensive control in this guide — DNC pre-flight, audit-logged consent overrides,
            A2P 10DLC monitoring, instant opt-out, quiet-hours enforcement — is built into iSpeedToClose
            and runs on every outbound message. No setup, no scrubbing service to integrate.
            Free 14-day trial, no credit card.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?utm_source=tcpa_guide_cta" className="btn-money !text-[15px]" data-testid="ebook-final-cta-register">
              Start free <ArrowRight size={14} />
            </Link>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer"
               className="btn-ghost text-white border-white/30 hover:bg-white/10"
               data-testid="ebook-final-cta-download">
              <Download size={14} /> Just give me the PDF
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[var(--ink)] text-white/70 py-10 mt-10">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
            <AssignFlowLogo size={14} />
          </span>
          <span className="font-display font-bold text-white">iSpeedToClose</span>
          <span className="text-white/40">·  the operating system for wholesalers</span>
        </div>
        <div className="flex items-center gap-5 text-xs">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/compare" className="hover:text-white">Compare</Link>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
